import React, { useEffect, useState } from 'react';
import { Images } from 'lucide-react';
import { Button, EmptyState, TextInput } from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useMyPageStrings } from '../myPageStrings';
import { LIMITS, galleryMedia, maxItems } from '../MyPage.shared';
import { ErrorLine, ImageSlot, RowActions, useFieldStyle } from '../parts/common';

// «گالری تصاویر» — up to 12 images, each with MANDATORY alt text (it is what a
// screen reader and a search engine read, and the service refuses an image
// without one). The alt is asked for BEFORE the picker opens, so an image can
// never be attached without it.

function GalleryTile({ media, index, count, busy, readOnly, onAlt, onMove, onRemove }) {
  const s = useMyPageStrings();
  const fieldStyle = useFieldStyle();
  const [alt, setAlt] = useState(media.alt || '');
  useEffect(() => { setAlt(media.alt || ''); }, [media.alt]);
  const changed = alt.trim() !== (media.alt || '');
  return (
    <div data-testid={`my-page-gallery-item-${media.id}`} style={{ border: `1px solid ${C.border}`, borderRadius: RADIUS.card, padding: '0.625rem', display: 'grid', gap: '0.5rem', background: C.card }}>
      <img src={media.url} alt={media.alt || ''} style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: RADIUS.control, background: C.bg }} />
      <TextInput
        label={s('myPage.gallery.alt')}
        value={alt}
        onChange={(e) => setAlt(e.target.value)}
        maxLength={LIMITS.alt_max}
        disabled={readOnly}
        error={!alt.trim() ? s('myPage.gallery.altRequired') : undefined}
        inputStyle={fieldStyle}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', justifyContent: 'space-between' }}>
        <Button size="sm" variant="outline" disabled={readOnly || busy || !changed || !alt.trim()} onClick={() => onAlt(media.id, alt.trim())}>
          {s('myPage.gallery.saveAlt')}
        </Button>
        <RowActions
          index={index} count={count} disabled={readOnly || busy}
          onMove={(i, d) => onMove(i, d)} onRemove={() => onRemove(media)}
          removeLabel={s('myPage.image.remove')}
          testId={`my-page-gallery-item-${media.id}`}
        />
      </div>
    </div>
  );
}

export default function GalleryEditor({ page, actions, cards, readOnly }) {
  const s = useMyPageStrings();
  const { formatNumber } = useTranslation();
  const fieldStyle = useFieldStyle();
  const items = galleryMedia(page);
  const max = maxItems('gallery');
  const [newAlt, setNewAlt] = useState('');
  const card = cards.gallery || {};
  const busy = !!card.saving;

  // Swapping two neighbours = two sort-order writes; the second answer is the page.
  const move = async (index, delta) => {
    const other = items[index + delta];
    const self = items[index];
    if (!other || !self) return;
    const first = await actions.updateImage('gallery', self.id, { sortOrder: other.sortOrder ?? index + delta });
    if (first.ok) await actions.updateImage('gallery', other.id, { sortOrder: self.sortOrder ?? index });
  };

  return (
    <div style={{ display: 'grid', gap: '1rem' }} data-testid="my-page-gallery">
      <div style={{ fontSize: '0.75rem', color: items.length > max ? C.red : C.textSecondary }}>
        {s('myPage.items.count', { count: formatNumber(items.length), max: formatNumber(max) })}
      </div>
      {items.length ? (
        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 13rem), 1fr))' }}>
          {items.map((m, i) => (
            <GalleryTile
              key={m.id} media={m} index={i} count={items.length} busy={busy} readOnly={readOnly}
              onAlt={(id, alt) => actions.updateImage('gallery', id, { alt })}
              onMove={move}
              onRemove={(media) => actions.removeImage('gallery', media.id)}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<Images size={44} strokeWidth={1.5} aria-hidden />}
          title={s('myPage.gallery.emptyTitle')}
          description={s('myPage.gallery.emptyHint')}
          style={{ padding: '1.5rem 1rem' }}
        />
      )}
      <ErrorLine error={card.error} testId="my-page-gallery-error" />

      {items.length < max && !readOnly ? (
        <div style={{ border: `1px dashed ${C.border}`, borderRadius: RADIUS.card, padding: '0.875rem', display: 'grid', gap: '0.75rem' }}>
          <TextInput
            label={s('myPage.gallery.newAlt')}
            placeholder={s('myPage.gallery.newAltPlaceholder')}
            value={newAlt}
            onChange={(e) => setNewAlt(e.target.value)}
            maxLength={LIMITS.alt_max}
            inputStyle={fieldStyle}
            data-testid="my-page-gallery-new-alt"
          />
          <ImageSlot
            slot="gallery"
            busy={!!cards['img-gallery']?.saving}
            disabled={!newAlt.trim()}
            hint={!newAlt.trim() ? s('myPage.gallery.altFirst') : undefined}
            onUpload={async (image) => {
              const res = await actions.uploadImage('img-gallery', { slot: 'gallery', image, alt: newAlt.trim() });
              if (res.ok) setNewAlt('');
              return res.ok;
            }}
            testId="my-page-gallery-add"
          />
          <ErrorLine error={cards['img-gallery']?.error} testId="my-page-gallery-add-error" />
        </div>
      ) : null}
    </div>
  );
}
